import Download16 from '@carbon/icons-react/es/download/16';
import { faTelescope } from '@fortawesome/pro-duotone-svg-icons';
import { ColDef } from 'ag-grid-community';
import { Col, Row, Tooltip } from 'antd';
import { keyBy } from 'lodash';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { FormInstanceApi, FormQuestionType } from 'api/form';
import { Button } from 'components/atoms/button';
import {
  EmptyState,
  EmptyStateContainer as EmptyStateContainerAtom,
  EmptyStateIcon,
} from 'components/atoms/empty-state';
import { Grid } from 'components/atoms/grid';
import { PageLoading } from 'components/atoms/loading';
import {
  assigneeRenderer,
  CreatedByRenderer,
  CreatedOnRenderer,
  dueDateRenderer,
  FormCellRenderer,
  headerRenderer,
  MoreOptionsRenderer,
  UpdatedByRenderer,
  UpdatedOnRenderer,
} from 'components/organisms/projects/form-responses/cell-renderer';
import { useTranslate } from 'hooks/use-translation';
import { useProjectsQuery } from 'queries';
import {
  useQueryAllAnswersByFormTemplate,
  useQueryAllInstancesByFormTemplate,
} from 'queries/form-instance';
import { FormInstanceName } from './instance-name';
import { FormProjectNameRenderer } from './project-name';

const EmptyStateContainer = styled(EmptyStateContainerAtom)`
  && {
    text-align: left;
  }
`;

export const Content = styled.div`
  flex-direction: column;

  flex: auto;
  min-height: 0;
  height: 100%;
  width: 100%;
`;

const TableWrapper = styled.div`
  height: calc(100% - 40px);
`;
const Table = styled(Grid)`
  &&& .ag-row {
    border-bottom: 1px solid var(--gray-20);
  }

  &&& .ag-overlay-no-rows-wrapper {
    display: block;
  }

  .ag-pinned-right-header,
  .ag-cell.ag-cell-first-right-pinned:not(.ag-cell-range-left):not(.ag-cell-range-single-cell) {
    border-left: 0px;
  }
`;
const FullWidthCellWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  color: var(--gray-50);
`;

const ExportButton = styled(Button)`
  margin-right: var(--spacing-06);
  padding: 0;
  aspect-ratio: 1;

  > svg {
    margin: auto;
  }
`;
const FullWidthCellRenderer = () => {
  return (
    <FullWidthCellWrapper>
      <FormattedMessage id="projects.form_responses.no_answers.title" />
    </FullWidthCellWrapper>
  );
};

export const NoResponsesEmptyState = () => {
  return (
    <EmptyStateContainer>
      <EmptyState
        image={<EmptyStateIcon icon={faTelescope} />}
        title={
          <FormattedMessage id="projects.form_responses.no_answers.title" />
        }
        message={
          <FormattedMessage id="projects.form_responses.no_answers.message" />
        }
        secondaryCTA={null}
      />
    </EmptyStateContainer>
  );
};

export const FormTemplateResponsesTable = ({
  formTemplateId,
}: {
  formTemplateId: number;
}) => {
  const { data: formTemplateDetails, status: formTemplateApiStatus } =
    useQueryAllAnswersByFormTemplate(formTemplateId);
  const { data: instanceData, status: templateInstancesApiStatus } =
    useQueryAllInstancesByFormTemplate(formTemplateId);

  const { data: projects } = useProjectsQuery();

  const projectsMap = useMemo(() => {
    return keyBy(projects, 'projectId');
  }, [projects]);

  const instanceDataMap = useMemo(() => {
    return instanceData ? keyBy(instanceData.instances, 'formInstanceId') : {};
  }, [instanceData]);

  const translate = useTranslate();

  /**
   * Transformations below to convert these into the formates supported by Ag-grid
   * Please refer the types below to understand more
   */
  const columns = useMemo(() => {
    if (formTemplateDetails) {
      const { questionsList: formQuestions } = formTemplateDetails;

      const moreActions: ColDef[] = [
        {
          headerName: translate('created_by'),
          cellRenderer: 'CreatedByRenderer',
          menuTabs: [],
        },
        {
          headerName: translate('created_on'),
          cellRenderer: 'CreatedOnRenderer',
          menuTabs: [],
        },
        {
          headerName: translate('updated_by'),
          cellRenderer: 'UpdatedByRenderer',
          menuTabs: [],
        },
        {
          headerName: translate('updated_on'),
          cellRenderer: 'UpdatedOnRenderer',
          menuTabs: [],
        },
        {
          field: 'more-actions',
          headerName: '',
          cellRenderer: 'MoreOptionsRenderer',
          maxWidth: 50,
          menuTabs: [],
          pinned: 'right',
        },
      ];

      return [
        {
          field: 'projectId',
          headerName: 'Project Name',
          cellRenderer: 'FormProjectNameRenderer',
          rowGroup: true,
          hide: true,
          cellRendererParams: {
            projectsMap,
            suppressCount: true,
          },
        },
        {
          field: 'formInstanceId',
          headerName: 'Form Instance',
          cellRenderer: 'FormInstanceName',
          rowGroup: true,
          hide: true,
          cellRendererParams: {
            instanceDataMap,
          },
        },
        {
          headerName: translate('due_date'),
          cellRenderer: 'dueDateRenderer',
          menuTabs: [],
        },
        {
          headerName: translate('assignees'),
          cellRenderer: 'assigneeRenderer',
          menuTabs: [],
        },
        ...formQuestions
          .filter(
            (question) =>
              question.formQuestionType !== FormQuestionType.TextStatement,
          )
          .map(
            ({
              questionId,
              questionText,
              formQuestionType,
              questionMeta = [],
              options = [],
              isDeleted,
            }) =>
              ({
                field: questionId.toString(),
                headerName: questionText,
                cellRenderer: 'responseCellRenderer',
                cellRendererParams: {
                  questionType: formQuestionType,
                  questionOptions: options,
                  questionMeta,
                  questionDeleted: isDeleted,
                },
                headerComponent: 'headerRenderer',
              } as ColDef),
          ),
        ...moreActions,
      ];
    }

    return [];
  }, [formTemplateDetails, projectsMap, instanceDataMap, translate]);

  const data = useMemo(() => {
    if (formTemplateDetails?.answers) {
      const answersList = formTemplateDetails.answers.map(
        (eachAnswerInstance) => {
          const {
            projectId,
            formInstanceId,
            answerMetaJson = {},
            answerId,
            createdAt,
            createdBy,
            updatedAt,
            updatedBy,
            taskId,
            dueDate,
            completedAt,
            assignee,
          } = eachAnswerInstance;

          const answerData = {
            projectId,
            formInstanceId,
            formTemplateId,
            answerId,
            createdAt,
            createdBy,
            updatedAt,
            updatedBy,
            taskId,
            dueDate,
            completedAt,
            assignee,
          };

          Object.values(answerMetaJson).forEach(({ questionId, answers }) => {
            if (
              typeof answers === 'object' &&
              answers !== null &&
              !Array.isArray(answers)
            ) {
              Object.keys(answers).forEach((question) => {
                answerData[question] = answers[question];
              });
            }
            answerData[questionId] = answers;
          });

          return answerData;
        },
      );

      return answersList;
    }

    return [];
  }, [formTemplateDetails, formTemplateId]);

  const apiStatus: 'idle' | 'error' | 'loading' | 'success' = useMemo(() => {
    if (
      formTemplateApiStatus === 'loading' ||
      templateInstancesApiStatus === 'loading'
    ) {
      return 'loading';
    }

    if (
      formTemplateApiStatus === 'success' &&
      templateInstancesApiStatus === 'success'
    ) {
      return 'success';
    }

    if (
      formTemplateApiStatus === 'error' ||
      templateInstancesApiStatus === 'error'
    ) {
      return 'error';
    }

    return 'idle';
  }, [formTemplateApiStatus, templateInstancesApiStatus]);

  const downloadButtonText = translate('download');

  const exportAsCSV = () => {
    FormInstanceApi.exportFormTemplateResponseAsCSV(formTemplateId).then(
      (downloadUrl) => {
        window.open(downloadUrl, '_blank');
      },
    );
  };

  switch (apiStatus) {
    case 'loading': {
      return <PageLoading />;
    }
    case 'success': {
      if (columns.length) {
        return (
          <Content>
            <Row justify="end" align="middle">
              <Col flex="32px">
                <Tooltip title={downloadButtonText}>
                  <ExportButton
                    type="text"
                    size="small"
                    onClick={() => exportAsCSV()}
                  >
                    <Download16 />
                  </ExportButton>
                </Tooltip>
              </Col>
            </Row>
            <TableWrapper>
              <Table
                className="ag-theme-balham"
                frameworkComponents={{
                  responseCellRenderer: FormCellRenderer,
                  FormProjectNameRenderer,
                  FormInstanceName,
                  emptyState: NoResponsesEmptyState,
                  FullWidthCellRenderer,
                  CreatedByRenderer,
                  UpdatedByRenderer,
                  MoreOptionsRenderer,
                  CreatedOnRenderer,
                  UpdatedOnRenderer,
                  dueDateRenderer,
                  headerRenderer,
                  assigneeRenderer,
                }}
                rowData={data}
                autoGroupColumnDef={{
                  headerName: 'Form details',
                  minWidth: 400,
                  cellRendererParams: {
                    suppressCount: true,
                  },
                  pinned: true,
                  menuTabs: [],
                }}
                columnDefs={columns}
                isFullWidthCell={(rowNode) => {
                  return rowNode.data ? !rowNode.data.answerId : false;
                }}
                fullWidthCellRenderer="FullWidthCellRenderer"
                suppressContextMenu
                noRowsOverlayComponentFramework={NoResponsesEmptyState}
                animateRows
                suppressMovableColumns
                defaultColDef={{
                  editable: false,
                  lockPinned: true,
                  flex: 1,
                  minWidth: 150,
                  resizable: true,
                }}
                groupDefaultExpanded={2}
              />
            </TableWrapper>
          </Content>
        );
      }

      return <></>;
    }

    case 'idle':
    case 'error':
    default: {
      return <></>;
    }
  }
};
